import type { KoszykCeny } from '@/hooks/useHistogramCen';
import { granicaZBazy } from '@/lib/skalaCeny';

/**
 * SŁUPKI HISTOGRAMU NA GRANICACH SKALI SUWAKA (28.09, suwak nieliniowy).
 *
 * Każdy krok suwaka to jeden koszyk: granice idą do bazy (`p_granice`, przesunięte o 1 zł —
 * patrz `GRANICE_DO_BAZY` w skalaCeny.ts), więc słupek `i` to oferty z ceną w (G[i], G[i+1]],
 * a na ekranie wszystkie słupki mają RÓWNĄ szerokość — tak jak kroki suwaka.
 *
 * Do 28.09 koszyki szły od p1 do p99 danych (ok. 1 900–7 400 zł) i front kładł je na skali po
 * kwotach. Zgłoszenie Piotra: zakres 1 000–1 900 zł, lista 39 ofert, zero słupków — tańsze od
 * p1 lądowały w koszyku zaczynającym się przy 1 900.
 *
 * OGONY: wiersz z `dol = null` (≤ 1 000 zł) dolicza się do pierwszego słupka, z `gora = null`
 * (> 8 000 zł) do ostatniego — tak jak skraje suwaka znaczą „bez granicy". Suma słupków jest
 * więc liczbą ofert z ceną (tożsamość pilnowana przez `scripts/check-histogram.mjs`).
 */
export type Zaznaczenie = 'pelne' | 'czesciowe' | 'brak';

export type Slupek = {
  /** Granice słupka na skali (bez ogonów): oferty z ceną w (dol, gora]. */
  dol: number;
  gora: number;
  ile: number;
};

export function slupkiNaGranicach(koszyki: KoszykCeny[], granice: readonly number[]): Slupek[] {
  const n = granice.length - 1;
  if (n < 1) return [];
  const slupki: Slupek[] = Array.from({ length: n }, (_, i) => ({ dol: granice[i], gora: granice[i + 1], ile: 0 }));
  const indeks = new Map(granice.map((g, i) => [g, i] as const));
  for (const k of koszyki) {
    const dol = granicaZBazy(k.dol);
    if (dol == null) {
      slupki[0].ile += k.ile; // ogon poniżej skali
      continue;
    }
    if (k.gora == null) {
      slupki[n - 1].ile += k.ile; // ogon od ostatniej granicy
      continue;
    }
    const i = indeks.get(dol);
    // Koszyk o granicach spoza skali oznacza rozjazd granic front ↔ baza (inne wywołanie,
    // stara funkcja). Nie zgadujemy, gdzie go położyć.
    if (i == null || i >= n) continue;
    slupki[i].ile += k.ile;
  }
  return slupki;
}

/**
 * Czy słupek `i` jest w wybranym zakresie. Zakres jak w filtrze listy: `od` i `do` INKLUZYWNE,
 * `undefined` = bez granicy.
 *
 * - `pelne`: KAŻDA oferta słupka spełnia filtr — przy uchwytach na krokach suwaka suma
 *   pełnych słupków to dokładnie liczba ofert na liście (poza dolnym krańcem, niżej).
 * - `czesciowe`: słupek zachodzi na zakres tylko częścią. Dwa przypadki: kwota wpisana w pole
 *   wypada w środku słupka, albo „od X" z uchwytu — słupek (X−krok, X] zawiera oferty za równe
 *   X zł, które lista pokazuje (a w koszykach 88–97% ofert ma cenę równo na granicy).
 * - `brak`: nic wspólnego.
 * Pierwszy słupek zawiera ogon poniżej skali, ostatni — ogon powyżej; stąd warunki na krańcach.
 */
export function zaznaczenieSlupka(
  i: number,
  slupki: Slupek[],
  rentMin: number | undefined,
  rentMax: number | undefined,
): Zaznaczenie {
  const s = slupki[i];
  const pierwszy = i === 0;
  const ostatni = i === slupki.length - 1;
  // Najniższa i najwyższa cena, jaka może być w słupku (ogony = nieskończoność).
  const najnizsza = pierwszy ? -Infinity : s.dol + 1;
  const najwyzsza = ostatni ? Infinity : s.gora;
  const od = rentMin ?? -Infinity;
  const doKwoty = rentMax ?? Infinity;
  if (najwyzsza < od || najnizsza > doKwoty) {
    // Wyjątek „od X": słupek (X−krok, X] kończy się równo na X — zawiera oferty za X zł.
    return najwyzsza === od ? 'czesciowe' : 'brak';
  }
  return najnizsza >= od && najwyzsza <= doKwoty ? 'pelne' : 'czesciowe';
}
